import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { CalendarCheck, Download, Loader2, Lock, Plus, Trash2 } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, fmtDate, fmtDateTime } from './design';

// Each month of the financial year: due date (moved by a granted extension), whether it was reported,
// what DEX refused, and the sign-off: someone compares Rostery's sessions and clients with DEX's own
// report for the month (My reports in the DEX portal), and a difference needs an explanation. The
// evidence CSV lists every session with its DEX ids and what DEX said. Periods close off 30 days
// after they end; DSS can re-open one in exceptional circumstances.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fyNow = () => { const d = new Date(); const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-${String(y + 1).slice(2)}`; };
// An extension's state in words, not the stored outcome value.
const EXTENSION = { requested: 'extension asked for', granted: 'extension granted', refused: 'extension refused' };

// A download that still shows the server's message when it fails (a blob hides it otherwise).
async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = href; a.download = name; a.click(); window.URL.revokeObjectURL(href);
  } catch (e) {
    let msg = 'Could not download it';
    try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* not JSON */ }
    toast.error(msg);
  }
}

function SignOff({ month, onDone }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ dexSessions: '', dexClients: '', note: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/aged-care/dex/months/${month}`).then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.message || 'Could not load the month')); }, [month]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/aged-care/dex/months/${month}/signoff`, { dexSessions: Number(f.dexSessions), dexClients: Number(f.dexClients), note: f.note });
      toast.success(r.data.differs ? 'Signed off with the difference explained' : 'Signed off: DEX matches'); onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not sign it off'); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-[12px] text-slate-500 py-2"><Loader2 size={13} className="inline animate-spin" /> Adding up the month…</p>;
  const t = d.totals;
  const differs = f.dexSessions !== '' && f.dexClients !== '' && (Number(f.dexSessions) !== t.sessions || Number(f.dexClients) !== t.clients);
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2 text-[12.5px]">
      <p className="text-slate-700">Rostery reported <b>{t.sessions}</b> session{t.sessions === 1 ? '' : 's'} for <b>{t.clients}</b> client{t.clients === 1 ? '' : 's'}
        {t.byService.length ? `: ${t.byService.map((x) => `${x.service} ${x.sessions}`).join(', ')}` : ''}.
        {t.excluded ? ` ${t.excluded} participant${t.excluded === 1 ? ' was' : 's were'} left out until fixed.` : ''}</p>
      <FormRow cols={2}>
        <Field label="Sessions in DEX's report">{(id) => <input id={id} type="number" min="0" className="input-field" value={f.dexSessions} onChange={(e) => setF((p) => ({ ...p, dexSessions: e.target.value }))} />}</Field>
        <Field label="Clients in DEX's report">{(id) => <input id={id} type="number" min="0" className="input-field" value={f.dexClients} onChange={(e) => setF((p) => ({ ...p, dexClients: e.target.value }))} />}</Field>
      </FormRow>
      {differs && (
        <Field label="Why they differ">{(id) => <textarea id={id} rows={2} className="input-field" value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} placeholder="For example, two sessions refused for a postcode, fixed and sent on 12 November" />}</Field>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost btn-sm" onClick={onDone}>Cancel</button>
        <button type="button" className="btn-primary btn-sm" disabled={busy || f.dexSessions === '' || f.dexClients === ''} onClick={save}>Sign off</button>
      </div>
    </div>
  );
}

export default function DexMonths() {
  const confirm = useConfirm();
  const [fy, setFy] = useState(fyNow());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [ext, setExt] = useState(null);
  const [reopens, setReopens] = useState([]);
  const [ro, setRo] = useState(null);
  // A failed load says so: "No months to show yet" would claim nothing is due.
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/dex/months', { params: { fy } }).then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e.response?.data?.message || 'Could not load the months'); });
    api.get('/aged-care/dex/reopens').then((r) => setReopens(r.data || [])).catch(() => setReopens([]));
  }, [fy]);
  useEffect(() => { load(); }, [load]);
  const saveExt = async () => {
    try {
      if (ext.id) await api.patch(`/aged-care/dex/extensions/${ext.id}`, { outcome: ext.outcome, newDue: ext.newDue, reference: ext.reference, note: ext.note });
      else {
        const r = await api.post('/aged-care/dex/extensions', { month: ext.month, requestedOn: ext.requestedOn, newDue: ext.newDue, reference: ext.reference, note: ext.note });
        if (r.data.warning) toast(r.data.warning, { icon: '⚠️', duration: 8000 });
      }
      toast.success('Saved'); setExt(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const saveRo = async () => {
    try { await api.post('/aged-care/dex/reopens', ro); toast.success('Re-opening recorded'); setRo(null); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  // A re-opening recorded by mistake kept the period open for changes; it can now be taken off,
  // after saying what that does.
  const removeRo = async (r) => {
    const ok = await confirm(`${fmtDate(r.period_start)} to ${fmtDate(r.period_end)} is treated as closed off again: its sessions can no longer be changed or sent to DEX.`,
      { title: 'Remove this re-opening?', confirmLabel: 'Remove re-opening', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/dex/reopens/${r.id}`); toast.success('Re-opening removed'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove it'); }
  };
  // The DSS decision opens on what was recorded, note included, so the note can be added to or
  // corrected when the answer comes; one still waiting opens as granted, the usual answer.
  const decide = (x) => setExt({ ...x, newDue: x.new_due || '', reference: x.reference || '', note: x.note || '', outcome: x.outcome === 'requested' ? 'granted' : x.outcome });
  const years = [0, 1, 2].map((n) => { const y = Number(fyNow().slice(0, 4)) - n; return `${y}-${String(y + 1).slice(2)}`; });
  return (
    <Panel title="Months: reported and signed off" icon={CalendarCheck} className="mt-5" bodyClass="px-4 py-3 space-y-2 text-[13px]"
      actions={(
        <select aria-label="Financial year" className="input-field !w-auto !py-1" value={fy} onChange={(e) => setFy(e.target.value)}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      )}>
      <p className="text-[12px] text-slate-500">Each month is due by the 14th of the next (or the date of a granted extension). Sign it off once DEX's own report for the month matches what Rostery sent.</p>
      {error && <p role="alert" className="text-red-800">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>}
      {!error && !data && <p className="text-slate-500"><Loader2 size={13} className="inline animate-spin -mt-0.5" /> Loading the months…</p>}
      {data && (data.months || []).length === 0 && <p className="text-slate-500">No months to show yet.</p>}
      <ul className="divide-y divide-slate-100">
        {(data?.months || []).map((m) => (
          <li key={m.month} className="py-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="min-w-0">
                <span className="font-medium text-slate-800">{monthLabel(m.month)}</span>
                {m.locked && <span className="ml-2 text-[11.5px] text-slate-500"><Lock size={11} className="inline -mt-0.5" /> closed off</span>}
                <span className="block text-[12px] text-slate-600">
                  Due {fmtDate(m.due)}{m.extension ? ` (${EXTENSION[m.extension.outcome] || 'extension'})` : ''} ·{' '}
                  {m.reported ? <span className="text-emerald-700">{m.reported.how === 'web services' ? 'sent by web services' : m.reported.how} {fmtDate(m.reported.at)}</span> : <span className="text-amber-700">not reported</span>}
                  {m.failures ? <span className="text-red-800"> · {m.failures} refused by DEX</span> : null}
                  {' · '}{m.signoff ? <span className="text-emerald-700">signed off {fmtDateTime(m.signoff.signed_at)}{m.signoff.signed_by_name ? ` by ${m.signoff.signed_by_name}` : ''}</span> : <span className="text-slate-500">not signed off</span>}
                </span>
                {m.extension?.note && <span className="block text-[12px] text-slate-500">Extension note: {m.extension.note}</span>}
              </span>
              <span className="flex flex-wrap gap-1">
                <button type="button" className="btn-ghost btn-sm" onClick={() => download(`/aged-care/dex/months/${m.month}/evidence.csv`, `dex-evidence-${m.month}.csv`)}><Download size={13} /> Evidence</button>
                {!m.extension && !m.reported && <button type="button" className="btn-ghost btn-sm" onClick={() => setExt({ month: m.month, requestedOn: isoToday(), newDue: '', reference: '', note: '' })}>Extension</button>}
                {m.extension && !(ext && ext.month === m.month) && (
                  <button type="button" className="btn-ghost btn-sm" onClick={() => decide(m.extension)}>{m.extension.outcome === 'requested' ? 'Record DSS decision' : 'Change DSS decision'}</button>
                )}
                {open !== m.month && m.reported && <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen(m.month)}>{m.signoff ? 'Sign off again' : 'Check and sign off'}</button>}
              </span>
            </div>
            {open === m.month && <SignOff month={m.month} onDone={() => { setOpen(null); load(); }} />}
            {ext && ext.month === m.month && (
              <div className="mt-2 rounded-lg border border-slate-200 p-3 space-y-2">
                <FormRow cols={3}>
                  {!ext.id && <Field label="Asked DSS on">{(id) => <input id={id} type="date" className="input-field" value={ext.requestedOn} onChange={(e) => setExt((p) => ({ ...p, requestedOn: e.target.value }))} />}</Field>}
                  <Field label="New due date">{(id) => <input id={id} type="date" className="input-field" value={ext.newDue || ''} onChange={(e) => setExt((p) => ({ ...p, newDue: e.target.value }))} />}</Field>
                  <Field label="DSS reference">{(id) => <input id={id} className="input-field" value={ext.reference || ''} onChange={(e) => setExt((p) => ({ ...p, reference: e.target.value }))} />}</Field>
                  {ext.id && (
                    <Field label="DSS decided">
                      {(id) => <select id={id} className="input-field" value={ext.outcome} onChange={(e) => setExt((p) => ({ ...p, outcome: e.target.value }))}><option value="granted">Granted</option><option value="refused">Refused</option><option value="requested">Still waiting</option></select>}
                    </Field>
                  )}
                </FormRow>
                <Field label="Note" hint={ext.id ? 'What DSS said, or why it was asked for.' : 'Why the extension is needed.'}>
                  {(id) => <textarea id={id} rows={2} className="input-field" value={ext.note || ''} maxLength={1000} onChange={(e) => setExt((p) => ({ ...p, note: e.target.value }))} />}
                </Field>
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setExt(null)}>Cancel</button>
                  <button type="button" className="btn-primary btn-sm" onClick={saveExt}>{ext.id ? 'Record DSS decision' : 'Save'}</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <p className="text-[12.5px] font-medium text-slate-700">Periods DSS re-opened</p>
          {!ro && <button type="button" className="btn-ghost btn-sm" onClick={() => setRo({ periodDay: '', grantedOn: isoToday(), until: '', reference: '', note: '' })}><Plus size={13} /> Record a re-opening</button>}
        </div>
        {reopens.length === 0 && !ro && <p className="text-[12px] text-slate-500">None. After a period closes off, DEX takes data for it only if DSS re-opens it.</p>}
        {reopens.map((r) => (
          <div key={r.id} className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[12px] text-slate-600 min-w-0">
              {fmtDate(r.period_start)} to {fmtDate(r.period_end)}: open until {fmtDate(r.until)}{r.reference ? ` (${r.reference})` : ''}{r.note ? `. ${r.note}` : ''}
            </p>
            <button type="button" className="btn-ghost btn-sm" onClick={() => removeRo(r)}><Trash2 size={13} /> Remove</button>
          </div>
        ))}
        {ro && (
          <div className="mt-2 rounded-lg border border-slate-200 p-3 space-y-2">
            <FormRow cols={2}>
              <Field label="A day in the period">{(id) => <input id={id} type="date" className="input-field" value={ro.periodDay} onChange={(e) => setRo((p) => ({ ...p, periodDay: e.target.value }))} />}</Field>
              <Field label="Open until">{(id) => <input id={id} type="date" className="input-field" value={ro.until} onChange={(e) => setRo((p) => ({ ...p, until: e.target.value }))} />}</Field>
              <Field label="Granted on">{(id) => <input id={id} type="date" className="input-field" value={ro.grantedOn} onChange={(e) => setRo((p) => ({ ...p, grantedOn: e.target.value }))} />}</Field>
              <Field label="DSS reference">{(id) => <input id={id} className="input-field" value={ro.reference} onChange={(e) => setRo((p) => ({ ...p, reference: e.target.value }))} />}</Field>
            </FormRow>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost btn-sm" onClick={() => setRo(null)}>Cancel</button>
              <button type="button" className="btn-primary btn-sm" onClick={saveRo}>Save</button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
